// React

// Packages

// Utilities
import { useInitiativeDataStore } from 'utilities/store';

// Hooks
import { useLabels } from 'utilities/hooks';

// ItemBuilder Contents
import ResultsAward from 'components/_itemBuilderContents/resultsAward';
import ResultsCompany from 'components/_itemBuilderContents/resultsCompany';
import ResultsCustomOutput from 'components/_itemBuilderContents/resultsCustomOutput';
import ResultsCustomOutputBasic from 'components/_itemBuilderContents/resultsCustomOutputBasic';
import ResultsDissemination from 'components/_itemBuilderContents/resultsDissemination';
import ResultsInfluenceOnPolicy from 'components/_itemBuilderContents/resultsInfluenceOnPolicy';
import ResultsIntellectualProperty from 'components/_itemBuilderContents/resultsIntellectualProperty';
import ResultsInventionDisclosure from 'components/_itemBuilderContents/resultsInventionDisclosure';
import ResultsKnowledge from 'components/_itemBuilderContents/resultsKnowledge';
import ResultsPatents from 'components/_itemBuilderContents/resultsPatents';
import ResultsProductsAndInventions from 'components/_itemBuilderContents/resultsProductsAndInventions';
import ResultsResearch from 'components/_itemBuilderContents/resultsResearch';

// Card Contents
import ResultsAwardCard from 'components/_cardContents/resultsAward';
import ResultsCompanyCard from 'components/_cardContents/resultsCompany';
import ResultsDisseminationCard from 'components/_cardContents/resultsDissemination';
import ResultsInfluenceCard from 'components/_cardContents/resultsInfluenceOnPolicy';
import ResultsInnovationCard from 'components/_cardContents/resultsInnovation';
import ResultsIntellectualPropertyCard from 'components/_cardContents/resultsIntellectualProperty';
import ResultsInventionDisclosureCard from 'components/_cardContents/resultsInventionDisclosure';
import ResultsKnowledgeCard from 'components/_cardContents/resultsKnowledge';
import ResultsOutputCard from 'components/_cardContents/resultsCustomOutput';
import ResultsPatentsCard from 'components/_cardContents/resultsPatents';
import ResultsProductsAndInventionsCard from 'components/_cardContents/resultsProductsAndInventions';
import ResultsResearchCard from 'components/_cardContents/resultsResearch';

const useResultHelpers = () => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { CONSTANTS, utilities } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { getValueLabel } = useLabels();

    // ///////////////////
    // DATA
    // ///////////////////

    const itemBuilderMap = {
        [CONSTANTS.RESULTS.AWARD]: ResultsAward,
        [CONSTANTS.RESULTS.COMPANY]: ResultsCompany,
        [CONSTANTS.RESULTS.CUSTOM_OUTPUT]: {
            basic: ResultsCustomOutputBasic,
            default: ResultsCustomOutput,
        },
        [CONSTANTS.RESULTS.DISSEMINATION]: ResultsDissemination,
        [CONSTANTS.RESULTS.INFLUENCE_ON_POLICY]: ResultsInfluenceOnPolicy,
        [CONSTANTS.RESULTS.INTELLECTUAL_PROPERTY]: ResultsIntellectualProperty,
        [CONSTANTS.RESULTS.INVENTION_DISCLOSURE]: ResultsInventionDisclosure,
        [CONSTANTS.RESULTS.KNOWLEDGE]: ResultsKnowledge,
        [CONSTANTS.RESULTS.PATENTS]: ResultsPatents,
        [CONSTANTS.RESULTS
            .PRODUCTS_AND_INVENTIONS]: ResultsProductsAndInventions,

        [CONSTANTS.RESULTS.RESEARCH]: ResultsResearch,
        // Legacy
        [CONSTANTS.RESULTS.INNOVATION]: null,
    };

    const cardMap = {
        [CONSTANTS.RESULTS.AWARD]: ResultsAwardCard,
        [CONSTANTS.RESULTS.COMPANY]: ResultsCompanyCard,
        [CONSTANTS.RESULTS.CUSTOM_OUTPUT]: ResultsOutputCard,
        [CONSTANTS.RESULTS.DISSEMINATION]: ResultsDisseminationCard,
        [CONSTANTS.RESULTS.INFLUENCE_ON_POLICY]: ResultsInfluenceCard,
        [CONSTANTS.RESULTS
            .INTELLECTUAL_PROPERTY]: ResultsIntellectualPropertyCard,
        [CONSTANTS.RESULTS
            .INVENTION_DISCLOSURE]: ResultsInventionDisclosureCard,
        [CONSTANTS.RESULTS.KNOWLEDGE]: ResultsKnowledgeCard,
        [CONSTANTS.RESULTS.PATENTS]: ResultsPatentsCard,
        [CONSTANTS.RESULTS
            .PRODUCTS_AND_INVENTIONS]: ResultsProductsAndInventionsCard,
        [CONSTANTS.RESULTS.RESEARCH]: ResultsResearchCard,
        // Legacy
        [CONSTANTS.RESULTS.INNOVATION]: ResultsInnovationCard,
    };

    const subTypeMap = {
        [CONSTANTS.RESULTS.AWARD]: 'Award_Type__c',
        [CONSTANTS.RESULTS.COMPANY]: 'Company_Type__c',
        [CONSTANTS.RESULTS.CUSTOM_OUTPUT]: null,
        [CONSTANTS.RESULTS.DISSEMINATION]: 'Publication_Method__c',
        [CONSTANTS.RESULTS.INFLUENCE_ON_POLICY]: 'Influence_Type__c',
        [CONSTANTS.RESULTS.INTELLECTUAL_PROPERTY]: null,
        [CONSTANTS.RESULTS.INVENTION_DISCLOSURE]: 'Invention_Type__c',
        [CONSTANTS.RESULTS.KNOWLEDGE]: 'Knowledge_Type__c',
        [CONSTANTS.RESULTS.PATENTS]: 'Patent_Status__c',
        [CONSTANTS.RESULTS.PRODUCTS_AND_INVENTIONS]: 'Intervention_Method__c',
        [CONSTANTS.RESULTS.RESEARCH]: 'Research_Importance__c',
        // Legacy
        [CONSTANTS.RESULTS.INNOVATION]: null,
    };

    // ///////////////////
    // METHODS
    // ///////////////////

    function getItemBuilderComponent(resultId) {
        const result = utilities.results.get(resultId);
        if (!result) return {};

        return getItemBuilderComponentFromType(result.Type__c);
    }

    function getItemBuilderComponentFromType(resultType) {
        // Need to check for intervention type based on initiative configuration type
        if (resultType === CONSTANTS.RESULTS.CUSTOM_OUTPUT) {
            return utilities.initiative.get().Configuration_Type__c ===
                CONSTANTS.INITIATIVE.BASIC
                ? itemBuilderMap[CONSTANTS.RESULTS.CUSTOM_OUTPUT].basic
                : itemBuilderMap[CONSTANTS.RESULTS.CUSTOM_OUTPUT].default;
        }

        return itemBuilderMap[resultType];
    }

    function getCardComponent(resultId) {
        const result = utilities.results.get(resultId);
        if (!result) return null;

        return cardMap[result.Type__c] ?? null;
    }

    function getTypeLabels(resultId) {
        const result = utilities.results.get(resultId);
        if (!result) return {};

        return {
            type: getValueLabel('Initiative_Result__c.Type__c', result.Type__c),
            subType: subTypeMap[result.Type__c]
                ? getValueLabel(
                      `Initiative_Result__c.${subTypeMap[result.Type__c]}`,
                      result[subTypeMap[result.Type__c]]
                  )
                : null,
        };
    }

    // ///////////////////
    // RETURN
    // ///////////////////

    return {
        getItemBuilderComponent,
        getItemBuilderComponentFromType,
        getCardComponent,
        getTypeLabels,
    };
};

export default useResultHelpers;
